import * as React from "react";
import { cn } from "@/lib/utils";
import { Dot } from "./dot";
import { getSocket } from "@/lib/ws-client";
import { useSocketEffect } from "@/lib/use-socket-effect";

export type RealtimeStatus = "realtime" | "reconnecting" | "offline";

// Carência antes de declarar "Offline": evita piscar a cada microqueda de rede
// (o socket.io-client reconecta sozinho).
export const RECONNECTING_GRACE_MS = 15_000;

function computeInitial(): RealtimeStatus {
  const s = getSocket();
  if (!s) return "offline";
  return s.connected ? "realtime" : "reconnecting";
}

export function useRealtimeStatus(): RealtimeStatus {
  const [status, setStatus] = React.useState<RealtimeStatus>(computeInitial);

  // useSocketEffect (re)liga ao socket atual no mount e sempre que o singleton muda —
  // cobre o caso de o badge (descendente do Topbar) montar antes do socket existir.
  useSocketEffect((socket) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    // Só arma após uma queda/erro REAL — não na conexão inicial em curso.
    const armOffline = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        setStatus("offline");
      }, RECONNECTING_GRACE_MS);
    };

    const onConnect = () => {
      clearTimer();
      setStatus("realtime");
    };
    const onDrop = () => {
      setStatus("reconnecting");
      armOffline();
    };

    if (!socket) {
      setStatus("offline");
      return;
    }
    socket.on("connect", onConnect);
    socket.on("disconnect", onDrop);
    socket.on("connect_error", onDrop);
    if (socket.connected) {
      setStatus("realtime");
    } else {
      // Conexão inicial em curso: fica "Reconectando" SEM armar a carência — um
      // primeiro handshake lento não deve virar falso "Offline" num login saudável.
      // Só um connect_error (falha real) ou um disconnect dispara o caminho de offline. (#6)
      setStatus("reconnecting");
    }
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDrop);
      socket.off("connect_error", onDrop);
      clearTimer();
    };
  }, []);

  return status;
}

const TONE = {
  realtime: {
    dot: "success" as const,
    label: "Tempo real",
    title: "A tela está recebendo atualizações automáticas ao vivo.",
  },
  reconnecting: {
    dot: "warning" as const,
    label: "Reconectando",
    title: "A conexão de tempo real caiu; tentando reconectar…",
  },
  offline: {
    dot: "danger" as const,
    label: "Offline",
    title:
      "A tela parou de receber atualizações automáticas. Os dados podem estar desatualizados. " +
      "Isto não significa que as máquinas ou a fábrica estão fora.",
  },
};

export interface RealtimeBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  status?: RealtimeStatus;
}

export const RealtimeBadge = React.forwardRef<HTMLDivElement, RealtimeBadgeProps>(
  ({ status, className, ...rest }, ref) => {
    const observed = useRealtimeStatus();
    const s = status ?? observed;
    const tone = TONE[s];
    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        data-status={s}
        title={tone.title}
        className={cn(
          "flex h-[30px] items-center gap-1.5 rounded-md px-2",
          "border border-border-default text-[12px] text-text-secondary",
          className
        )}
        {...rest}
      >
        <Dot tone={tone.dot} halo={false} pulse={s === "realtime"} size={6} />
        <span className="font-mono">{tone.label}</span>
      </div>
    );
  }
);
RealtimeBadge.displayName = "RealtimeBadge";
